import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { Colors } from '../../../ui';
import { Badge } from '../../../ui/Badge';
import { Position } from '../../../ui/Position';
import { useResolve } from '../../Container';
import { BoardItem } from '../../common/BoardItem';
import { Hangout } from '../../hangouts/Hangout';
import { LiveBoardInfo } from '../elements/BoardParticipantPreview';
import { useBoardThumb } from '../elements/BoardThumb';
import { NewBoardActionMenuButton } from './NewBoardActionMenuButton';

const useBoardInfoQuery = (boardId: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['boards.info', boardId],
        queryFn: () => api.exec(v2.boards.info(boardId)),
    });
};

export const LiveBoard = React.memo<{ boardId: string }>(({ boardId }) => {

    const { data: board } = useBoardInfoQuery(boardId);
    const thumbUrl = useBoardThumb(board);

    if (!board) return null;
    const isPrivate = !board.podId;
    const path = `workspace:${board.workspaceId}/board:${boardId}`;
    const label = isPrivate ? 'Private Board' : board.label;

    return <BoardItem key={board.id} clickable>
        <BoardItem.Header>
            <BoardItem.Label title={label}>
                {label}
            </BoardItem.Label>
            <BoardItem.ActionMenu>
                <NewBoardActionMenuButton boardId={boardId} />
            </BoardItem.ActionMenu>
        </BoardItem.Header>
        <Link to={`~/${board.id}`}>
            <BoardItem.Main>
                <BoardItem.Image>
                    <BoardItem.Thumb src={thumbUrl} />
                </BoardItem.Image>
                <LiveBoardInfo path={path} />
                <Position at="topLeft" inset=".25">
                    <Badge color={Colors.positive}>LIVE</Badge>
                </Position>
            </BoardItem.Main>
        </Link>
        <BoardItem.Footer>
            <BoardItem.Date>
                Created {board.createdAt.toLocaleDateString()}
            </BoardItem.Date>
        </BoardItem.Footer>
    </BoardItem>;
});

export const useLiveBoardsForWorkspace = (workspaceId: string) => {
    const hangout = useResolve(Hangout);
    const clients = useObserver(hangout.clients);
    const [ticker, setTicker] = useState(0);
    useEffect(() => hangout.on('clientUpdate', ([_client, keys]) => {
        if (keys.includes('location')) setTicker(x => x + 1);
    }), []);
    return useMemo(() => {
        const boards = Array.from(clients)
            .filter(x => x?.location?.test(`workspace:${workspaceId}/board:*`))
            .map(x => x?.location?.param ?? null)
            .filter(x => !!x);
        return Array.from(new Set(boards));
    }, [workspaceId, clients, ticker]);
};
